import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { pushClickDismisser, useEscapeDismiss } from './dismiss-stack';

export const useDismiss = (
	open: boolean,
	onClose: () => void,
	refs: RefObject<HTMLElement | null>[],
	{ closeOnClickOutside = true, closeOnEscape = true, closeOnBlur = false } = {}
) => {
	// Callers pass `refs` as an inline array literal and `onClose` is often a
	// fresh closure, so both change identity every render. Read them through
	// refs and key the effect on `open` alone: the click-dismisser must stay
	// registered for the popover's WHOLE open lifetime. If the effect re-ran on
	// every render, each re-render would re-push the entry to the END of the
	// dismiss stack — and since parent effects re-run after child effects, any
	// shared re-render would move an ANCESTOR popover above its descendant.
	// The ancestor would then treat clicks in the descendant's (portaled)
	// content as "outside" and close the whole popover chain mid-click.
	const refsRef = useRef(refs);
	refsRef.current = refs;
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (!open || !closeOnClickOutside) return;

		// Predicate that asks: "Is this click target inside one of MY refs?".
		// Registered on the global click-dismiss stack so ancestor popovers
		// can see clicks in our content as "still inside" their subtree.
		const isInside = (target: Node) =>
			refsRef.current.some((r) => r.current && r.current.contains(target));

		const { isInsideSelfOrLater, unregister } = pushClickDismisser(isInside);

		const handlePointerDown = (e: PointerEvent) => {
			// Don't close when the click landed inside us OR inside any
			// descendant popover that opened later (its content is portaled
			// outside our DOM tree, but logically belongs to us).
			if (isInsideSelfOrLater(e.target as Node)) return;
			onCloseRef.current();
		};

		// Use pointerdown in capture phase so it fires before any
		// stopPropagation() in bubble-phase handlers (e.g. Popover triggers).
		// setTimeout ensures we don't dismiss on the same event that opened.
		const tid = setTimeout(
			() => document.addEventListener('pointerdown', handlePointerDown, true),
			0,
		);
		return () => {
			clearTimeout(tid);
			document.removeEventListener('pointerdown', handlePointerDown, true);
			unregister();
		};
	}, [open, closeOnClickOutside]);

	useEscapeDismiss(open && closeOnEscape, onClose);

	// Close when the browser tab/window loses focus
	useEffect(() => {
		if (!open || !closeOnBlur) return;

		const handleWindowBlur = () => onClose();
		window.addEventListener('blur', handleWindowBlur);
		return () => window.removeEventListener('blur', handleWindowBlur);
	}, [open, closeOnBlur, onClose]);
};
